import { useCallback, useEffect } from 'react';

import { getPasskey, authenticate } from '../store/actions/auth2Fa.actions';
import { useAuth2FaContext, STEPS } from '../context/Auth2FaContext';

import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { use2FaConfig } from './use2FaConfig';

export function useGetPasskey() {
  const dispatch = useAppDispatch();
  const {
    accessTokenDecoded: { secureKeyId },
    secureKey: passkey,
  } = useCurrentSupportUser();

  const isLoading = useAppSelector((state) => state.auth.isLoading);

  const { setCurrentStep } = useAuth2FaContext();
  const { scaApi, supportUserId } = use2FaConfig();

  useEffect(() => {
    if (!passkey && secureKeyId) {
      dispatch(getPasskey(secureKeyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secureKeyId]);

  const authenticate2FA = useCallback(async () => {
    const challenge = JSON.stringify({
      iat: Date.now(),
      supportUserId,
    });

    const assertion = await scaApi.get(
      {
        type: 'webauthn',
        options: {
          userHandle: passkey?.authenticator?.userHandle,
          challenge,
          allowCredentials: [
            {
              id: passkey.authenticator?.publicKeyCredentialId,
              type: passkey.authenticator?.type,
              transports: passkey.authenticator?.transports,
            },
          ],
        },
      },
      undefined,
      async (error) => {
        console.error(error);
        setCurrentStep(STEPS.AUTH_FAIL);
      },
    );

    if (typeof assertion !== 'string' && assertion?.webauthn) {
      dispatch(
        authenticate(assertion.webauthn, (status) => {
          if (status === 'fail') {
            setCurrentStep(STEPS.AUTH_FAIL);
          }
        }),
      );
    }
  }, [passkey, scaApi, supportUserId, dispatch, setCurrentStep]);

  return { isLoading, isDisabled: !passkey, authenticate2FA };
}
